import classNames from 'classnames'
import { DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { Box, EmptyCell, Icon, Typography, Avatar, type IColumn, Tooltip } from 'shared/ui'
import { type RowInbox } from 'entities/Inbox/model/Table/RowInbox'
import { type IUser } from 'entities/Users'
import styles from './styles.module.scss'

export const getInboxesColumns = (): IColumn<RowInbox>[] => [
  {
    field: 'name',
    name: 'Name',
    isSortable: true,
    isDisabledChangeVisible: true,
    maxWidth: '260px',
    renderRowCell: (row) => <NameCell row={row} />,
  },
  {
    field: 'owner',
    name: 'Owner',
    maxWidth: '128px',
    isDisabledChangeVisible: true,
    isSortable: true,
    renderRowCell: (row) => (
      <span className={styles.ownerCell}>
        <Typography
          ellipsis
          variant={'body-md-regular'}
          ariaLabel={'formattedValue'}
          tooltipProps={{
            label: row.owner?.full_name,
          }}
        >
          {row.owner?.full_name}
        </Typography>
      </span>
    ),
  },
  {
    field: 'members',
    name: 'Members',
    maxWidth: '150px',
    isDisabledChangeVisible: true,
    isSortable: true,
    renderRowCell: (row) => {
      const members = row.members
      const count = members.length
      if (count === 0) {
        return <EmptyCell />
      }

      const visibleMembers = members.slice(0, 3) as IUser[]

      return (
        <Box display='flex' alignItems='center' gap='8px'>
          <div
            className={classNames(styles.avatarWrapper, {
              [styles.avatarWrapperDisabled]: row.origin.disabled,
            })}
          >
            {visibleMembers.map((member, index) => (
              <Avatar
                key={member.id}
                info={member.avatarInfo}
                size={16}
                fontSize={10}
                className={index > 0 ? styles.avatarOverlap : ''}
              />
            ))}
          </div>
          <Typography
            variant='body-md-regular'
            ariaLabel='totalMembers'
            style={{ marginLeft: '8px' }}
          >
            {count} member{count > 1 ? 's' : ''}
          </Typography>
        </Box>
      )
    },
  },
  {
    field: 'created_at',
    name: 'Created at',
    isSortable: true,
    renderRowCell: (row) =>
      row.createdAt ? (
        <Typography variant='body-md-regular' ariaLabel='createdAt'>
          {uiStore.dayjs(row.createdAt).format(DayjsFormats.full6)}
        </Typography>
      ) : (
        <EmptyCell />
      ),
  },
]

const NameCell = ({ row }: { row: RowInbox }) => {
  const count = row.numbersCount
  const hasAircall = row.hasAircallNumber
  const firstNumber = row.firstNumber
  const lock = row.lock
  const disabled = row.origin.disabled

  const renderNumbersInfo = () => {
    if (count === 1) {
      return (
        <Box display='flex' alignItems='center' gap='4px'>
          <Typography variant='body-sm-regular' ariaLabel='number' color='tertiary'>
            {firstNumber?.formatted_number}
          </Typography>
          {hasAircall && <Icon icon='aircall' />}
        </Box>
      )
    }

    if (count > 1) {
      return (
        <Typography variant='body-sm-regular' ariaLabel='numbersCount' color='tertiary'>
          {count} numbers
        </Typography>
      )
    }

    return <EmptyCell />
  }

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      alignItems='center'
      gap='4px'
      margin='10px 0'
    >
      <Box display='flex' gap='8px' alignItems='center'>
        {lock && disabled && (
          <Tooltip label={lock.tooltip} placement={'top'}>
            <Icon icon={'lock'} fontSize={20} color='var(--content-primary-tertiary)' />
          </Tooltip>
        )}
        <div className={styles.cellWrapper}>
          <Typography
            variant='body-md-regular'
            ariaLabel='inboxName'
            ellipsis
            tooltipProps={{
              label: row.origin.name,
            }}
          >
            {row.origin.name}
          </Typography>
          {renderNumbersInfo()}
        </div>
      </Box>
      {row.isDefaultTeam && (
        <Tooltip
          label='The flagged inbox is your default inbox. You can change it anytime using the ··· menu.'
          placement={'top'}
          breakWord
        >
          <Icon icon={'flagFilled'} color={'var(--content-attention-primary)'} />
        </Tooltip>
      )}
    </Box>
  )
}
